import i18n from '@/i18n/config'

/**
 * Поля пользователя, из которых собирается подпись.
 *
 * Шире, чем `User`: подписывать приходится и вложенных пользователей из
 * других ресурсов (участник урока, ученик домашки, получатель программы),
 * у которых часть полей не приходит.
 */
export interface UserNameSource {
  id?: number | null
  surname?: string | null
  first_name?: string | null
  middle_name?: string | null
  display_name?: string | null
  email?: string | null
}

export interface UserDisplayNameOptions {
  /**
   * Что главнее.
   *
   * - `display_name` (по умолчанию) — подпись от API: учитель видит ник, который
   *   сам дал ученику; так подписаны списки учеников;
   * - `full_name` — «Фамилия Имя»: так подписаны заголовки и крошки карточки,
   *   где рядом стоит сам ник.
   */
  prefer?: 'display_name' | 'full_name'
  /** Добавить отчество к «Фамилия Имя» */
  withMiddleName?: boolean
}

/** Пусто для null, undefined и строки из одних пробелов. */
function clean(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * «Фамилия Имя» (с отчеством по запросу). Пустая строка, если ФИО не заполнено.
 */
export function formatUserFullName(
  user: UserNameSource | null | undefined,
  withMiddleName = false,
): string {
  if (!user) return ''

  const parts = [clean(user.surname), clean(user.first_name)]
  if (withMiddleName) {
    parts.push(clean(user.middle_name))
  }

  return parts.filter((part) => part !== '').join(' ')
}

/**
 * Подпись пользователя в интерфейсе — единственное место, где решается,
 * чем подписать человека без ФИО.
 *
 * Почта в запасное имя больше не годится: у ученика без кабинета её нет
 * (`email: null`). Запасной вариант — `display_name` из API: он уже учитывает
 * ник, ФИО, почту и «Ученик #id». Когда `display_name` не пришёл (вложенные
 * пользователи других ресурсов), дальше идут почта и «Ученик #id».
 *
 * Пустая строка — только когда пользователя нет вовсе.
 */
export function getUserDisplayName(
  user: UserNameSource | null | undefined,
  options: UserDisplayNameOptions = {},
): string {
  if (!user) return ''

  const { prefer = 'display_name', withMiddleName = false } = options
  const fullName = formatUserFullName(user, withMiddleName)
  const displayName = clean(user.display_name)

  const candidates = prefer === 'full_name'
    ? [fullName, displayName]
    : [displayName, fullName]

  for (const candidate of [...candidates, clean(user.email)]) {
    if (candidate !== '') return candidate
  }

  return user.id != null ? i18n.t('users.fallbackName', { id: user.id }) : ''
}

/**
 * Ученик без кабинета: в EasyA не входит, почты и пароля нет.
 *
 * Строгое сравнение: признак приходит только из UserResource, у вложенных
 * пользователей других ресурсов его нет — это не «без кабинета».
 */
export function hasNoCabinet(user: { has_cabinet?: boolean | null } | null | undefined): boolean {
  return user?.has_cabinet === false
}
